import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Button,
  Divider,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import { fromZonedTime, toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { FinanceGranularity } from '../../../services/financeReportsService';
import { useAuth } from '../../../context/AuthContext';
import DateRangePicker, {
  DateRangePreset,
  DateRange as PickerRange,
} from '../../common/DateRangePicker';

export interface DateRange {
  from: Date;
  to: Date;
  label: string;
}

interface FinanceFilterBarProps {
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
  granularity: FinanceGranularity;
  onGranularityChange: (g: FinanceGranularity) => void;
}

/**
 * Compose a Date from wall-clock parts interpreted in the school's timezone.
 * The returned Date is the correct *UTC instant* for that wall-clock in
 * that TZ. Sent to the API as ISO, Mongo's $gte/$lte then match correctly
 * against the UTC-stored `date` field.
 *
 * Without this the browser's local timezone leaks into the query and users
 * near midnight see "today's" expenses missing from the last-30-days window.
 */
const zonedDate = (
  timezone: string,
  y: number,
  m: number,
  d: number,
  h: number,
  min: number,
  s: number,
  ms: number
): Date => {
  const wall = new Date(y, m, d, h, min, s, ms);
  return fromZonedTime(wall, timezone);
};

const zonedNowParts = (timezone: string) => {
  const nowInTz = toZonedTime(new Date(), timezone);
  return {
    y: nowInTz.getFullYear(),
    m: nowInTz.getMonth(),
    d: nowInTz.getDate(),
  };
};

/**
 * Preset windows — all boundaries anchored to the SCHOOL's timezone so that
 * "Today" means "today for the school", regardless of where the admin's
 * laptop happens to be. The Date objects returned are UTC instants that
 * bracket exactly one wall-clock day (or window) in the school's TZ.
 */
const buildPresets = (timezone: string): DateRange[] => {
  const { y, m, d } = zonedNowParts(timezone);

  const startOfDay = (offsetDays: number) => zonedDate(timezone, y, m, d - offsetDays, 0, 0, 0, 0);
  const endOfDay = (offsetDays: number) => zonedDate(timezone, y, m, d - offsetDays, 23, 59, 59, 999);

  const zonedToday = new Date(zonedDate(timezone, y, m, d, 0, 0, 0, 0));
  const dayOfWeek = toZonedTime(zonedToday, timezone).getDay(); // 0..6
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const startOfMonth = zonedDate(timezone, y, m, 1, 0, 0, 0, 0);
  const startOfQuarter = zonedDate(timezone, y, Math.floor(m / 3) * 3, 1, 0, 0, 0, 0);
  const startOfYear = zonedDate(timezone, y, 0, 1, 0, 0, 0, 0);

  return [
    { label: 'Today', from: startOfDay(0), to: endOfDay(0) },
    { label: 'Yesterday', from: startOfDay(1), to: endOfDay(1) },
    { label: 'Last 7 days', from: startOfDay(6), to: endOfDay(0) },
    { label: 'Last 30 days', from: startOfDay(29), to: endOfDay(0) },
    { label: 'Last 90 days', from: startOfDay(89), to: endOfDay(0) },
    { label: 'Week to date', from: startOfDay(mondayOffset), to: endOfDay(0) },
    { label: 'Month to date', from: startOfMonth, to: endOfDay(0) },
    { label: 'Quarter to date', from: startOfQuarter, to: endOfDay(0) },
    { label: 'Year to date', from: startOfYear, to: endOfDay(0) },
  ];
};

// ─── ISO adapters ───────────────────────────────────────────────────────────
// The shared DateRangePicker speaks yyyy-MM-dd strings; the finance page
// speaks TZ-aware `Date` bounds. These helpers translate at the boundary so
// the picker never has to know about timezones.

const dateToIso = (d: Date | null, timezone: string): string | null =>
  d ? formatInTimeZone(d, timezone, 'yyyy-MM-dd') : null;

/** yyyy-MM-dd → start-of-that-day in the school TZ (UTC instant). */
const isoToStart = (iso: string, timezone: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return zonedDate(timezone, y, m - 1, d, 0, 0, 0, 0);
};

/** yyyy-MM-dd → end-of-that-day in the school TZ (UTC instant, 23:59:59.999). */
const isoToEnd = (iso: string, timezone: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return zonedDate(timezone, y, m - 1, d, 23, 59, 59, 999);
};

const FinanceFilterBar: React.FC<FinanceFilterBarProps> = ({
  range,
  onRangeChange,
  granularity,
  onGranularityChange,
}) => {
  const theme = useTheme();
  const { timezone } = useAuth();

  const presets = useMemo(() => buildPresets(timezone), [timezone]);
  const defaultRange = presets.find(p => p.label === 'Last 30 days')!;

  // Feed the picker its own custom preset list. Each preset's `make()`
  // returns yyyy-MM-dd strings computed in the school TZ so the picker's
  // internal comparison ("does this match the current draft?") lines up
  // with the label the finance page cares about.
  const pickerPresets: DateRangePreset[] = useMemo(() => presets.map(p => ({
    key: p.label,
    label: p.label,
    make: () => ({
      from: dateToIso(p.from, timezone),
      to: dateToIso(p.to, timezone),
    }),
  })), [presets, timezone]);

  const pickerValue: PickerRange = {
    from: dateToIso(range.from, timezone),
    to: dateToIso(range.to, timezone),
  };

  const handlePickerChange = (r: PickerRange) => {
    if (!r.from || !r.to) return;  // picker won't fire until both are set
    const nextFrom = isoToStart(r.from, timezone);
    const nextTo = isoToEnd(r.to, timezone);
    // Match against a known preset to keep the sidebar highlight and any
    // downstream label-driven copy (e.g. "Last 30 days · Aug 21 – Sep 19")
    // aligned. Falls back to "Custom range" when the user picked freely.
    const matched = presets.find(p =>
      dateToIso(p.from, timezone) === r.from
      && dateToIso(p.to, timezone) === r.to
    );
    onRangeChange({
      from: nextFrom,
      to: nextTo,
      label: matched?.label ?? 'Custom range',
    });
  };

  const handleReset = () => {
    onRangeChange(defaultRange);
    onGranularityChange('day');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {/* Date range picker (same component used across the app) */}
      <DateRangePicker
        label={range.label}
        value={pickerValue}
        onChange={handlePickerChange}
        presets={pickerPresets}
        minWidth={280}
      />

      <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

      {/* Granularity */}
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel id="granularity-label">Granularity</InputLabel>
        <Select
          labelId="granularity-label"
          value={granularity}
          label="Granularity"
          onChange={(e) => onGranularityChange(e.target.value as FinanceGranularity)}
        >
          <MenuItem value="day">Daily</MenuItem>
          <MenuItem value="week">Weekly</MenuItem>
          <MenuItem value="month">Monthly</MenuItem>
          <MenuItem value="quarter">Quarterly</MenuItem>
          <MenuItem value="year">Yearly</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ flex: 1 }} />

      <Button
        size="small"
        startIcon={<RestartAlt fontSize="small" />}
        onClick={handleReset}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        Reset
      </Button>
    </Paper>
  );
};

export default FinanceFilterBar;
